import { useState, useEffect, useMemo } from "react";
import { supabase } from "../lib/supabaseClient";
import { useUserProfile } from "../contexts/UserProfileContext";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import ConsentBadge from "./ConsentBadge";

import { MOOD_IMAGES } from "../assets/mood";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  created_at: string | null;
  cover_image_url: string | null;
}



export default function BlogPosts({
  readonly = false,
  showCreateButton = true,
}: {
  readonly?: boolean;
  showCreateButton?: boolean;
}) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  // ⬇️ Kein Auto-Expand mehr: Start = alle zu
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);

  const { profile } = useUserProfile();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPosts = async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false }); // neueste zuerst

      if (error) {
        console.error("❌ Fehler beim Laden der Blogposts:", error.message);
      } else {
        setPosts(data || []);
      }
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const canEdit = profile?.role === "creator" || profile?.role === "admin";

  const oldestPost = useMemo(() => {
    if (posts.length === 0) return null;
    return posts.reduce((acc, cur) =>
      new Date(cur.created_at ?? "") < new Date(acc.created_at ?? "") ? cur : acc
    );
  }, [posts]);

  const otherPosts = useMemo(() => {
    if (!oldestPost) return posts;
    return posts.filter((p) => p.id !== oldestPost.id);
  }, [posts, oldestPost]);

  return (
    <div className="bg-[#0d1117]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <svg aria-hidden="true" className="w-full h-full opacity-70" preserveAspectRatio="none" viewBox="0 0 1440 560">
            <defs>
              <linearGradient id="grad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#0ea5e9" />
                <stop offset="100%" stopColor="#38bdf8" />
              </linearGradient>
            </defs>
            <path
              d="M0,96L80,112C160,128,320,160,480,154.7C640,149,800,107,960,85.3C1120,64,1280,64,1360,64L1440,64L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
              fill="url(#grad)"
              opacity="0.15"
            />
          </svg>
        </div>

        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-10 pb-8 lg:pt-16 lg:pb-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Text */}
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide uppercase text-sky-400 bg-sky-500/20 border border-sky-500/30 rounded-full px-2.5 py-1">
              Svevana • Gemeinsam erleben
            </p>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-100 leading-tight">
              Reisen neu gedacht – übersichtlich, fair und stressfrei.
            </h1>
            <p className="mt-4 text-slate-300 max-w-xl">
              Organisiere Gruppenreisen ohne Chaos: Programm, Ausgaben, Dokumente & Kommunikation an einem Ort.
              Hier teilen wir Updates, Features und Ideen aus der Entwicklung.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#blog"
                className="inline-flex items-center rounded-lg bg-sky-500 text-white px-4 py-2.5 text-sm font-medium hover:bg-sky-600 transition-colors"
              >
                Neueste Beiträge lesen
              </a>

              {!readonly && canEdit && showCreateButton && (
                <button
                  onClick={() => navigate("/create-blog")}
                  className="inline-flex items-center rounded-lg bg-[#1e2639] text-slate-300 border border-[#2a3347] px-4 py-2.5 text-sm font-medium hover:bg-[#2a3347] transition-colors"
                >
                  + Neuer Blogbeitrag
                </button>
              )}
            </div>
          </div>

          {/* Mood-Collage */}
          <div className="relative lg:ml-6">
            <div className="grid grid-cols-3 gap-3 lg:gap-4">
            <MoodCard src={MOOD_IMAGES[0]} className="translate-y-6 rotate-[-2deg]" />
            <MoodCard src={MOOD_IMAGES[1]} className="translate-y-0 rotate-[1.5deg]" />
            <MoodCard src={MOOD_IMAGES[2]} className="-translate-y-4 rotate-[-1deg]" />
          </div>

            <div className="pointer-events-none absolute inset-x-8 -bottom-6 h-12 rounded-full blur-2xl bg-sky-400/20" />
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section id="blog" className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-6 mb-16">
        {loading ? (
          <SkeletonBlog />
        ) : posts.length === 0 ? (
          <p className="text-slate-500">Keine Beiträge vorhanden.</p>
        ) : (
          <>
            {/* Featured = ältester Post, ABER standardmäßig zu (kein Auto-Expand) */}
            {oldestPost && (
              <>
                <h2 className="text-xl font-semibold text-slate-100 mb-4">Neu im Blog</h2>
                <FeaturedPost
                  post={oldestPost}
                  expanded={expandedPostId === oldestPost.id}
                  onToggle={() =>
                    setExpandedPostId(expandedPostId === oldestPost.id ? null : oldestPost.id)
                  }
                  canEdit={!!(canEdit && !readonly)}
                  onEdit={() => navigate(`/edit-blog/${oldestPost.id}`)}
                />
              </>
            )}

            {/* Teaser-Grid */}
            {otherPosts.length > 0 && (
              <div className="mt-10">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">Weitere Beiträge</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherPosts.map((post) => {
                    const isExpanded = expandedPostId === post.id;
                    return (
                      <TeaserCard
                        key={post.id}
                        post={post}
                        expanded={isExpanded}
                        canEdit={!!(canEdit && !readonly)}
                        onEdit={() => navigate(`/edit-blog/${post.id}`)}
                        onToggle={() =>
                          setExpandedPostId(isExpanded ? null : post.id)
                        }
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </section>
       <ConsentBadge />
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function MoodCard({ src, className = "" }: { src: string; className?: string }) {
  return (
    <div className={`aspect-[3/4] rounded-xl overflow-hidden bg-[#161b27] shadow-lg ring-1 ring-white/5 ${className}`}>
      <img src={src} alt="" className="h-full w-full object-cover" />
    </div>
  );
}

function SkeletonBlog() {
  return (
    <div className="space-y-6">
      <div className="h-64 rounded-2xl bg-[#161b27] animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-56 rounded-xl bg-[#161b27] animate-pulse" />
        ))}
      </div>
    </div>
  );
}

function FeaturedPost({
  post,
  expanded,
  onToggle,
  canEdit,
  onEdit,
}: {
  post: BlogPost;
  expanded: boolean;
  onToggle: () => void;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <article className="relative overflow-hidden rounded-2xl border border-[#2a3347] bg-[#161b27] shadow-lg">
      {canEdit && (
        <button
          onClick={onEdit}
          className="absolute top-3 right-3 z-10 bg-amber-500 text-white text-xs py-1 px-2 rounded hover:bg-amber-600 transition-colors"
        >
          ✏️ Bearbeiten
        </button>
      )}

      {post.cover_image_url && (
        <div className="w-full h-[42vh] bg-[#1e2639] flex items-center justify-center">
          <img src={post.cover_image_url} alt={post.title} className="max-h-full max-w-full object-contain" />
        </div>
      )}

      <div className="p-6 sm:p-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-2">{post.title}</h2>
        <p className="text-slate-500 text-sm mb-4">{new Date(post.created_at ?? "").toLocaleDateString()}</p>

        <MarkdownContent content={post.content} expanded={expanded} />

        <button
          onClick={onToggle}
          className="mt-4 inline-flex items-center text-sm font-medium text-sky-400 hover:text-sky-300 hover:underline transition-colors"
        >
          {expanded ? "Weniger lesen" : "Mehr lesen"}
        </button>
      </div>
    </article>

  );
}

function TeaserCard({
  post,
  expanded,
  canEdit,
  onEdit,
  onToggle,
}: {
  post: BlogPost;
  expanded: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onToggle: () => void;
}) {
  return (
    <article className="relative flex flex-col overflow-hidden rounded-xl border border-[#2a3347] bg-[#161b27] shadow transition-colors hover:border-sky-500/20">
      {canEdit && (
        <button
          onClick={onEdit}
          className="absolute top-3 right-3 z-10 bg-amber-500 text-white text-xs py-1 px-2 rounded hover:bg-amber-600 transition-colors"
        >
          ✏️
        </button>
      )}

      {post.cover_image_url && (
        <div className="w-full h-44 bg-[#1e2639] flex items-center justify-center">
          <img src={post.cover_image_url} alt={post.title} className="max-h-full max-w-full object-contain" />
        </div>
      )}

      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-100">{post.title}</h3>
        <p className="text-slate-500 text-sm mb-3">{new Date(post.created_at ?? "").toLocaleDateString()}</p>

        <MarkdownContent content={post.content} expanded={expanded} />

        <button
          onClick={onToggle}
          className="mt-3 inline-flex items-center text-sm font-medium text-sky-400 hover:text-sky-300 hover:underline transition-colors"
        >
          {expanded ? "Weniger lesen" : "Mehr lesen"}
        </button>
      </div>
    </article>
  );
}

function MarkdownContent({ content, expanded }: { content: string; expanded: boolean }) {
  return (
    <div
      className={[
        "prose prose-invert prose-pre:overflow-x-auto max-w-none text-[0.95rem] break-words transition-all duration-300",
        expanded ? "max-h-none" : "max-h-40 overflow-hidden relative",
      ].join(" ")}
    >
      {!expanded && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#161b27] to-transparent" />}

      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          code({ className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || "");
            if (match) {
              return (
                <SyntaxHighlighter
                  style={atomDark}
                  language={match[1]}
                  PreTag="div"
                  wrapLongLines={true}
                  customStyle={{ overflowX: "auto", borderRadius: "0.5rem", margin: "0.75rem 0" }}
                  {...props}
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              );
            }
            return (
              <code className="bg-[#1e2639] text-sky-300 px-1 rounded break-words" {...props}>
                {children}
              </code>
            );
          },
          img({ src, alt }) {
            return (
              <span className="block w-full my-4 bg-[#1e2639]">
                {/* eslint-disable-next-line */}
                <img src={src || ""} alt={alt || ""} className="mx-auto max-h-[70vh] w-auto object-contain" />
              </span>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
